import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const PlatformContext = createContext();
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/*
  Central store for the whole SaaS platform.
  Now backed by MongoDB via /api/{policies,customers,services} — every
  device sees the same data. localStorage is used only as a fast local cache
  so the UI is populated instantly on page load while the network fetch
  refreshes it. All mutations are write-through: optimistic local update +
  a background POST to the backend.
*/

const load = (key, def) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? def; } catch { return def; }
};
const save = (key, val) => localStorage.setItem(key, JSON.stringify(val));

const authHeaders = () => {
  try {
    const admin = JSON.parse(localStorage.getItem('dhs-admin') || 'null');
    if (admin?.token) return { Authorization: `Bearer ${admin.token}` };
    const agent = JSON.parse(localStorage.getItem('dhs-agent') || 'null');
    if (agent?.token) return { Authorization: `Bearer ${agent.token}` };
  } catch {}
  return {};
};

const hasStaffToken = () => !!authHeaders().Authorization;

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  return res;
};

export const PlatformProvider = ({ children }) => {
  const [customers, setCustomers] = useState(() => load('dhs-customers', []));
  const [agents, setAgents] = useState(() => load('dhs-agents', []));
  const [policies, setPolicies] = useState(() => load('dhs-policies', []));
  const [services, setServices] = useState(() => load('dhs-services', []));
  const [cancellations, setCancellations] = useState(() => load('dhs-cancellations', []));

  // Keep localStorage cache in sync so a hard refresh feels instant.
  useEffect(() => save('dhs-customers', customers), [customers]);
  useEffect(() => save('dhs-agents', agents), [agents]);
  useEffect(() => save('dhs-policies', policies), [policies]);
  useEffect(() => save('dhs-services', services), [services]);
  useEffect(() => save('dhs-cancellations', cancellations), [cancellations]);

  // Refresh from backend on mount (and when a staff token appears).
  const refreshFromBackend = useCallback(async () => {
    if (!hasStaffToken()) return;
    try {
      const [pRes, cRes, sRes] = await Promise.all([
        apiFetch('/api/policies'),
        apiFetch('/api/customers'),
        apiFetch('/api/services'),
      ]);
      if (pRes.ok) setPolicies(await pRes.json());
      if (cRes.ok) setCustomers(await cRes.json());
      if (sRes.ok) setServices(await sRes.json());
    } catch {
      // Backend unreachable — keep the local cache.
    }
  }, []);

  useEffect(() => { refreshFromBackend(); }, [refreshFromBackend]);

  // Also re-sync when the user switches tabs (helps agents on shared devices).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') refreshFromBackend(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshFromBackend]);

  // Write-through helpers ------------------------------------------------
  const persistPolicy = (p) => { if (hasStaffToken()) apiFetch('/api/policies', { method: 'POST', body: JSON.stringify(p) }).catch(() => {}); };
  const persistCustomer = (c) => { if (hasStaffToken()) apiFetch('/api/customers', { method: 'POST', body: JSON.stringify(c) }).catch(() => {}); };
  const persistService = (s) => { if (hasStaffToken()) apiFetch('/api/services', { method: 'POST', body: JSON.stringify(s) }).catch(() => {}); };

  // ---- Customers ----
  const upsertCustomer = (c) => {
    let saved = null;
    setCustomers((list) => {
      const idx = list.findIndex((x) => x.id === c.id);
      if (idx >= 0) { const cp = [...list]; cp[idx] = { ...cp[idx], ...c }; saved = cp[idx]; return cp; }
      saved = { createdAt: new Date().toISOString(), status: 'Active', ...c };
      return [saved, ...list];
    });
    if (saved) persistCustomer(saved);
  };
  const deactivateCustomer = (id) => setCustomers((l) => l.map((c) => {
    if (c.id !== id) return c;
    const upd = { ...c, status: 'Deactivated' };
    persistCustomer(upd);
    return upd;
  }));
  const activateCustomer = (id) => setCustomers((l) => l.map((c) => {
    if (c.id !== id) return c;
    const upd = { ...c, status: 'Active' };
    persistCustomer(upd);
    return upd;
  }));

  // ---- Agents (kept as a local-only view; source of truth is the backend admin/agents endpoints) ----
  const upsertAgent = (a) => setAgents((list) => {
    const idx = list.findIndex((x) => x.id === a.id);
    if (idx >= 0) { const cp = [...list]; cp[idx] = { ...cp[idx], ...a }; return cp; }
    return [{ createdAt: new Date().toISOString(), status: 'Pending', ...a }, ...list];
  });
  const setAgentStatus = (id, status) => setAgents((l) => l.map((a) => a.id === id ? { ...a, status } : a));
  const removeAgent = (id) => setAgents((l) => l.filter((a) => a.id !== id));

  // ---- Policies ----
  const nextPlanNumber = () => {
    const cur = parseInt(localStorage.getItem('dhs-plan-counter') || '100001', 10);
    localStorage.setItem('dhs-plan-counter', String(cur + 1));
    return `DHS${String(cur).padStart(5, '0')}`;
  };

  // Same numbering scheme but non-mutating — used at checkout to PREVIEW the plan
  // number so we can show it as the customer's bank-transfer reference before the
  // policy record is actually created.
  const previewNextPlanNumber = () => {
    const cur = parseInt(localStorage.getItem('dhs-plan-counter') || '100001', 10);
    return `DHS${String(cur).padStart(5, '0')}`;
  };

  const createPolicy = (data) => {
    const planNumber = nextPlanNumber();
    const policy = {
      id: 'POL-' + planNumber, planNumber, createdAt: new Date().toISOString(),
      status: 'Active', paymentStatus: 'Payment Due', paymentEvents: [],
      documentsGeneratedAt: null,
      ...data,
    };
    setPolicies((l) => [policy, ...l]);
    persistPolicy(policy);
    return policy;
  };

  const _mutatePolicy = (id, fn) => setPolicies((l) => l.map((p) => {
    if (p.id !== id) return p;
    const upd = fn(p);
    persistPolicy(upd);
    return upd;
  }));

  const updatePolicy = (id, patch) => _mutatePolicy(id, (p) => ({ ...p, ...patch }));

  const deletePolicy = (id) => {
    setPolicies((l) => l.filter((p) => p.id !== id));
    if (hasStaffToken()) apiFetch(`/api/policies/${id}`, { method: 'DELETE' }).catch(() => {});
  };

  const setPaymentStatus = (id, status, note) => _mutatePolicy(id, (p) => {
    const evt = { at: new Date().toISOString(), status, note: note || '' };
    return { ...p, paymentStatus: status, paymentEvents: [...(p.paymentEvents || []), evt] };
  });

  /**
   * Record a partial payment against a policy. Appends to `p.partialPayments`,
   * recalculates cumulative paid-to-date and either:
   *   - flips status to 'Paid' when the running total meets/exceeds `p.amount`, or
   *   - keeps status as 'Partially Paid' and records the payment event.
   * Never mutates cancellation-related state — partial payments only apply to
   * regular plan premiums, matching the user's spec.
   *
   * Returns { status, paidToDate, outstanding, autoPaid } describing the result
   * so callers can chain follow-up UX (e.g. auto receipt email on full payment).
   */
  const recordPartialPayment = (id, amount, note) => {
    const value = Number(amount);
    if (!isFinite(value) || value <= 0) return { status: 'invalid', paidToDate: 0, outstanding: 0, autoPaid: false };
    let out = { status: 'partial', paidToDate: 0, outstanding: 0, autoPaid: false };
    _mutatePolicy(id, (p) => {
      // Guard: don't touch cancelled policies — cancellations have their own flow.
      if (p.status === 'Cancelled' || p.paymentStatus === 'Cancelled') return p;
      const at = new Date().toISOString();
      const prevPartials = Array.isArray(p.partialPayments) ? p.partialPayments : [];
      const nextPartials = [...prevPartials, { amount: Number(value.toFixed(2)), at, note: note || '' }];
      const paidToDate = nextPartials.reduce((s, pp) => s + Number(pp.amount || 0), 0);
      const total = Number(p.amount || 0);
      const outstanding = Math.max(0, +(total - paidToDate).toFixed(2));
      const rounded = +paidToDate.toFixed(2);
      const isFullyPaid = rounded + 0.005 >= total; // tolerance for float rounding
      const events = [...(p.paymentEvents || []), {
        at,
        status: isFullyPaid ? 'Paid' : 'Partially Paid',
        note: isFullyPaid
          ? `Final partial payment of £${value.toFixed(2)} received — plan fully paid (total £${rounded.toFixed(2)}).`
          : `Partial payment of £${value.toFixed(2)} received — £${outstanding.toFixed(2)} still outstanding.`,
      }];
      out = { status: isFullyPaid ? 'paid' : 'partial', paidToDate: rounded, outstanding, autoPaid: isFullyPaid };
      return {
        ...p,
        partialPayments: nextPartials,
        paymentStatus: isFullyPaid ? 'Paid' : 'Partially Paid',
        paymentEvents: events,
      };
    });
    return out;
  };

  const markDocumentsGenerated = (id) => _mutatePolicy(id, (p) => ({ ...p, documentsGeneratedAt: p.documentsGeneratedAt || new Date().toISOString() }));

  const cancelPolicy = (id, { amount, reason, cancelledAt, paymentDate }) => {
    const at = cancelledAt || new Date().toISOString();
    _mutatePolicy(id, (p) => ({
      ...p,
      // Cancellation is REQUESTED but not yet CONFIRMED — that only happens when
      // the admin marks the cancellation charge paid. Keep the plan active until
      // then so paperwork wording and revenue reporting stay accurate.
      status: 'Cancellation Requested',
      planPaidBeforeCancellation: p.paymentStatus === 'Paid',
      cancellation: { amount, reason, cancelledAt: at, paymentDate: paymentDate || p.paymentDue, confirmed: false },
      paymentEvents: [...(p.paymentEvents || []), { at, status: 'Cancellation Requested', note: reason || 'Cancellation requested — awaiting payment' }],
    }));
    setCancellations((l) => [{ id: 'CAN-' + id + '-' + Date.now(), policyId: id, amount, reason, cancelledAt: at }, ...l]);
  };

  const reactivatePolicy = (id) => _mutatePolicy(id, (p) => ({
    ...p, status: 'Active', paymentStatus: p.paymentEvents?.some((e) => e.status === 'Paid') ? 'Paid' : 'Payment Due', cancellation: null,
  }));

  const markCancellationPaid = (id) => {
    const at = new Date().toISOString();
    _mutatePolicy(id, (p) => {
      if (!p.cancellation) return p;
      return {
        ...p,
        // Finalise the cancellation now that the charge has been paid.
        status: 'Cancelled',
        paymentStatus: 'Cancelled',
        cancellation: { ...p.cancellation, paidAt: at, confirmed: true },
        paymentEvents: [...(p.paymentEvents || []), { at, status: 'Cancellation Paid', note: `Cancellation settlement £${Number(p.cancellation.amount || 0).toFixed(2)} — plan confirmed cancelled` }],
      };
    });
    setCancellations((l) => l.map((c) => c.policyId === id ? { ...c, paidAt: at } : c));
  };

  // ---- Services ----
  const upsertService = (s) => {
    let saved = null;
    setServices((list) => {
      const idx = list.findIndex((x) => x.id === s.id);
      if (idx >= 0) { const cp = [...list]; cp[idx] = { ...cp[idx], ...s }; saved = cp[idx]; return cp; }
      saved = { createdAt: new Date().toISOString(), status: 'Open', ...s };
      return [saved, ...list];
    });
    if (saved) persistService(saved);
  };
  const deleteService = (id) => {
    setServices((l) => l.filter((s) => s.id !== id));
    if (hasStaffToken()) apiFetch(`/api/services/${id}`, { method: 'DELETE' }).catch(() => {});
  };

  // Follow-up: policies whose docs were generated over 24h ago but not paid
  const followUps = policies.filter((p) => {
    if (!p.documentsGeneratedAt) return false;
    if (p.paymentStatus === 'Paid') return false;
    if (['Cancelled', 'Refunded'].includes(p.paymentStatus)) return false;
    const ms = Date.now() - new Date(p.documentsGeneratedAt).getTime();
    return ms > 24 * 60 * 60 * 1000;
  });

  const revenue = () => {
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startWeek = new Date(startToday); startWeek.setDate(startToday.getDate() - startToday.getDay());
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startYear = new Date(now.getFullYear(), 0, 1);
    let today = 0, week = 0, month = 0, year = 0, total = 0;
    const add = (amount, t) => {
      total += amount;
      if (t >= startToday.getTime()) today += amount;
      if (t >= startWeek.getTime()) week += amount;
      if (t >= startMonth.getTime()) month += amount;
      if (t >= startYear.getTime()) year += amount;
    };
    policies.forEach((p) => {
      // 1) Partial payments always count as revenue on the date they were received.
      //    (Applies to Partially Paid AND fully-Paid policies — the partial rows are audit-tracked
      //     even after the final settlement flips status to 'Paid'.)
      const partials = Array.isArray(p.partialPayments) ? p.partialPayments : [];
      partials.forEach((pp) => add(Number(pp.amount || 0), new Date(pp.at).getTime()));
      const partialsSum = partials.reduce((s, pp) => s + Number(pp.amount || 0), 0);

      // 2) Plan revenue: ONLY when the customer actually paid the plan (not the cancellation charge).
      //    A cancelled policy that was never paid before cancellation must not contribute plan revenue
      //    even if someone incorrectly flips the status dropdown to 'Paid' after cancellation.
      const planWasPaid = p.paymentStatus === 'Paid'
        || (p.paymentStatus === 'Cancelled' && p.planPaidBeforeCancellation);
      if (planWasPaid) {
        const paidEvt = [...(p.paymentEvents || [])].reverse().find((e) => e.status === 'Paid');
        if (paidEvt) {
          // Only add the REMAINING amount after subtracting any partial payments already counted above.
          const remainder = Math.max(0, +(Number(p.amount || 0) - partialsSum).toFixed(2));
          if (remainder > 0) add(remainder, new Date(paidEvt.at).getTime());
        }
      }
      // 3) Cancellation charge revenue is always separate — only counts if the cancellation
      //    settlement itself was marked paid.
      if (p.cancellation?.paidAt) {
        add(Number(p.cancellation.amount || 0), new Date(p.cancellation.paidAt).getTime());
      }
    });
    return { today, week, month, year, total };
  };

  const monthlySeries = () => {
    const now = new Date();
    const buckets = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString('en-GB', { month: 'short' }), year: d.getFullYear(), month: d.getMonth(), premium: 0, cancellation: 0, quotes: 0, cancelled: 0, services: 0 });
    }
    const bucketFor = (date) => buckets.find((b) => b.year === date.getFullYear() && b.month === date.getMonth());
    policies.forEach((p) => {
      const created = new Date(p.createdAt); const b = bucketFor(created); if (b) b.quotes += 1;
      // Partial payments hit the bucket of their `at` date.
      const partials = Array.isArray(p.partialPayments) ? p.partialPayments : [];
      partials.forEach((pp) => { const bp = bucketFor(new Date(pp.at)); if (bp) bp.premium += Number(pp.amount || 0); });
      const partialsSum = partials.reduce((s, pp) => s + Number(pp.amount || 0), 0);

      const planWasPaid = p.paymentStatus === 'Paid'
        || (p.paymentStatus === 'Cancelled' && p.planPaidBeforeCancellation);
      if (planWasPaid) {
        const paidEvt = [...(p.paymentEvents || [])].reverse().find((e) => e.status === 'Paid');
        if (paidEvt) {
          const bp = bucketFor(new Date(paidEvt.at));
          const remainder = Math.max(0, +(Number(p.amount || 0) - partialsSum).toFixed(2));
          if (bp && remainder > 0) bp.premium += remainder;
        }
      }
      if (p.cancellation?.cancelledAt) { const bc = bucketFor(new Date(p.cancellation.cancelledAt)); if (bc) bc.cancelled += 1; }
      if (p.cancellation?.paidAt) { const bp = bucketFor(new Date(p.cancellation.paidAt)); if (bp) bp.cancellation += Number(p.cancellation.amount || 0); }
    });
    services.forEach((s) => { const b = bucketFor(new Date(s.createdAt || Date.now())); if (b) b.services += 1; });
    return buckets;
  };

  return (
    <PlatformContext.Provider value={{
      customers, upsertCustomer, deactivateCustomer, activateCustomer,
      agents, upsertAgent, setAgentStatus, removeAgent,
      policies, createPolicy, updatePolicy, deletePolicy, setPaymentStatus, recordPartialPayment, markDocumentsGenerated, previewNextPlanNumber,
      cancelPolicy, reactivatePolicy, markCancellationPaid,
      services, setServices, upsertService, deleteService,
      cancellations, setCancellations,
      followUps, revenue, monthlySeries,
      refreshFromBackend,
    }}>
      {children}
    </PlatformContext.Provider>
  );
};

export const usePlatform = () => useContext(PlatformContext);
